<template lang="pug">
form.post-form.container(
  autocomplete="off"
  @submit.prevent="$emit('submit')"
)
  post-form-header(
    ref="title"
    :post="isDraft ? draft : post"
    @change="updateTitleOrSubtitle"
    @nextSection="focusFirstSection"
  )

  .post-content
    post-form-section(
      v-for="(section, index) in sections"
      :key="section.id"
      ref="sections"
      :section="section"
      :section-index="index"
      @change="updateSection"
      @nextSection="nextSection"
      @prevSection="prevSection"
      @newSection="newSection"
      @removeSection="removeSection"
      @click.native.stop=""
    )
</template>

<script>
import { mapMutations } from 'vuex'
import { getPostResourceMixin } from '@/mixins/postResource'
import DateHelpersMixin from '@/mixins/dateHelpers'
import Post from '@/models/Post'
import PostSection from '@/models/PostSection'
import FieldsetInput from '@/components/fieldset-input'
import FieldsetSection from '@/components/fieldset-section'
import FieldsetTextarea from '@/components/fieldset-textarea'
import PostFormHeader from '@/components/post/default-editor/post-form__title'
import PostFormSection from '@/components/post/default-editor/post-form__section'

const PostResourceMixin = getPostResourceMixin({
  postRequired: false,
})
const NEWLINE = '\n'

export default {
  name: 'post-form--default',
  mixins: [DateHelpersMixin, PostResourceMixin],
  components: {
    FieldsetInput,
    FieldsetSection,
    FieldsetTextarea,
    PostFormHeader,
    PostFormSection,
  },
  props: {
    errors: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      draftId: null,
      localDraft: undefined,
    }
  },
  computed: {
    isDraft() {
      return this.post == null
    },
    draft() {
      return this.localDraft
    },
    sections() {
      return this.localDraft.sections
    },
  },
  watch: {
    errors: {
      deep: true,
      handler(val) {
        if (val.title) {
          this.$refs.title.$refs.title.error(val.title)
        } else {
          this.$refs.title.$refs.title.error(null)
        }
        if (val.content) {
          this.$refs.sections[0].error(val.content)
        } else {
          this.$refs.sections[0].error(null)
        }
      }
    },
  },
  methods: {
    ...mapMutations(['addDraft', 'updateDraft']),
    updateTitleOrSubtitle({ title, subtitle }) {
      const changes = {}
      if (title) changes.title = title
      if (subtitle) changes.subtitle = subtitle
      Object.assign(this.localDraft, changes)
      this.$emit('change', this.draft)
    },
    updatePostOrDraft(payload) {
      console.debug('updatePostOrDraft', payload)
      if (this.isDraft) {
        this.updateDraft(Object.assign({ id: this.draftId }, payload))
      } else {
        this.updateDraft(
          Object.assign({ id: this.draftId, postId: this.post.id }, payload),
        )
      }
      this.$emit('change', this.draft)
    },
    focusFirstSection() {
      this.$refs.sections[0].focus()
    },
    async nextSection({ index, createSection = false }) {
      console.debug('nextSection', index)
      await this.$nextTick()
      // resize the current section
      this.$refs.sections[index].resize()

      if (!this.$refs.sections[index + 1] && createSection) {
        this.newSection({ after: index, content: '' })
      }
      // focus if we move
      if (this.$refs.sections[index + 1]) {
        this.$refs.sections[index + 1].focus()
      }
    },
    async prevSection({ index, content, moveContent = false }) {
      console.debug('prevSection', index, content)
      if (index > 0) {
        if (moveContent) {
          const section = this.sections[index - 1]
          this.updateSection({
            index: index - 1,
            content: section.content + NEWLINE + content,
          })
          this.removeSection({ index })
        }
        await this.$nextTick()
        this.$refs.sections[index - 1].resize()
        this.$refs.sections[index - 1].focus()
      } else if (index === 0) {
        this.$refs.title.focus(true)
      }
    },
    newSection({ after, content }) {
      console.debug('newSection', after, content)
      this.insertSection({
        index: after + 1,
        section: new PostSection({ content }),
      })
      this.nextSection({ index: after })
    },
    insertSection({ index, section }) {
      console.debug('insertSection', { index, section })
      this.sections.splice(index, 0, section)
      this.updatePostOrDraft({ sections: this.sections })
    },
    updateSection({ index, content }) {
      console.log('updateSection', { index, content })
      this.sections[index].content = content
      this.updatePostOrDraft({ sections: this.sections })
    },
    removeSection({ index }) {
      console.debug('removeSection', { index })
      this.sections.splice(index, 1)
      this.updatePostOrDraft({ sections: this.sections })
    },
    setFocus() {
      const index = this.sections.length
      if (index > 0) {
        this.$refs.sections[index - 1].focus()
      }
    },
    onSubmit(e) {
      e && e.preventDefault()
      this.$emit('submit', this.draft)
    },
  },
  created() {
    this.draftId = `draft_${+new Date()}` // timestamp
    let sections
    if (!this.isDraft) {
      sections = [].concat(
        this.post.sections.map(s => Object.assign({}, s)),
      )
    } else {
      sections = [new PostSection()]
    }
    this.localDraft = new Post({
      id: this.draftId,
      postId: !this.isDraft ? this.post.id : undefined,
      authorId: this.$currentUser.id,
      sections,
    })

    this.addDraft(this.localDraft.toJson())
    this.$emit('change', this.draft)
  },
}
</script>

<style lang="sass">
.post-form
  .fieldset__input
    --input-padding-v: 0px

.post-form
  height: calc(100vh - 3rem)

  // .oembed__header
  //   padding: var(--input-padding-v) var(--input-padding-h)
  //   font-size: var(--post-content-font-size)
  //   line-height: var(-post-content-line-height)

  .fieldset__input
    margin-left: calc(var(--input-padding-h) * -1)
    margin-right: calc(var(--input-padding-h) * -1)
    &.--wide
      width: calc(100% + var(--input-padding-h) * 2)

.post-header
  margin-top: 4rem

  .post-title,
  .post-subtitle
    font-family: var(--serif-fonts)

.post-content
  font-family: var(--serif-fonts)
  font-size: var(--post-content-font-size)
  line-height: var(--post-content-line-height)
  margin-top: 2rem
  margin-bottom: 4rem
  max-width: 80ch

.page__header
  &.--fixed
    background-image: linear-gradient(0deg, rgba(255,255,255,0), white 1.5rem, white)
    position: fixed
    top: 0;
    left: 0;
    right: 0;
    z-index: 99
    // container styles
    max-width: calc(96ch + 2rem)
    margin: 0 auto
    padding-bottom: 1rem
    
.save-button
  display: inline-block
  margin-bottom: 0.5rem
  font-size: 1rem
  font-variant: all-small-caps

  &.--underline-effect
    --underline-effect-line-height: 2px

</style>
